// filepath: apps/web/src/operator/OperatorTokenGate.tsx
//
// FR-UI-OPS-1 AC-2 — gate that prompts for the operator token
// when none is present, and renders children when the token is
// held in the in-memory store.
//
// `OperatorRoot` mounts this gate above the summary route. When
// the token is null, the prompt is shown; when the token is set,
// the children render (the summary route hits
// `GET /admin/summary` with the Bearer header).
//
// The gate is the only component that knows about the
// non-sensitive 401 error message — it watches its own attempt
// counter and resolves the i18n key on the 401th attempt. The
// server's error envelope is NEVER echoed (INV-5).

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { useOperatorToken } from "./useOperatorToken";
import { OperatorTokenPrompt } from "./OperatorTokenPrompt";

export interface OperatorTokenGateProps {
  children: React.ReactNode;
}

/**
 * OperatorTokenGate — gates the protected subtree behind the
 * in-memory operator token. When the token is null, the prompt
 * is shown. When the token is set, the children render. The
 * `promptNonce` increments after a 401 (driven by the gate
 * re-render on parent state); the error message resolves from
 * the i18n bundle.
 */
export function OperatorTokenGate({
  children,
}: OperatorTokenGateProps): React.ReactElement {
  const { t } = useTranslation();
  const { token, clear } = useOperatorToken();
  // The summary route calls `clear()` on 401. The gate listens
  // for that transition (token going from set → null) and
  // surfaces a non-sensitive error message for one render. The
  // counter only matters as a "did we just 401?" signal.
  const [showUnauthorized, setShowUnauthorized] = React.useState(false);
  const lastTokenRef = React.useRef<string | null>(token);

  React.useEffect(() => {
    const prev = lastTokenRef.current;
    lastTokenRef.current = token;
    // Cleared from set → null after a 401. Surface the i18n
    // error message on this transition only.
    if (prev !== null && token === null) {
      setShowUnauthorized(true);
    } else if (token !== null) {
      // Cleared back to a fresh token — hide the error.
      setShowUnauthorized(false);
    }
  }, [token]);

  // Defensive: expose `clear` so future extensions can mount an
  // explicit "sign out" button. The current build keeps the
  // gate minimal.
  void clear;

  if (token === null) {
    const errorMessage = showUnauthorized
      ? t(
          "operator.tokenPrompt.error.unauthorized",
          "Unauthorized. Please check your token and try again.",
        )
      : undefined;
    return (
      <OperatorTokenPrompt
        {...(errorMessage !== undefined ? { errorMessage } : {})}
      />
    );
  }
  return <>{children}</>;
}
